import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { HiringManagerOpenpositionsService } from '@app/pages/hiring-manager/hiring-manager-openpositions/hiring-manager-openpositions.service';
import { interval, takeWhile } from 'rxjs';
import { PositionLabels } from '@app/shared/constants/global.enum';
import { Candidate } from '@app/pages/vendor/add-candidate/add-candidate.model';

interface DonutSegment {
  color: string;
  dashArray: string;
  dashOffset: number;
}

const DONUT_RADIUS = 28;
const DONUT_CIRC = 2 * Math.PI * DONUT_RADIUS;

const POSITION_COLORS = {
  open: '#003F2D',
  onHold: '#d97706',
  closed: '#2563eb',
};

@Component({
  selector: 'app-positions',
  templateUrl: './positions.component.html',
  styleUrls: ['./positions.component.scss'],
})
export class PositionsComponent implements OnInit {
  priority = { grey: '#888' };
  fontSize_opentab = 1.2;

  PositionLabels = PositionLabels;

  allPositions: any[] = [];
  filteredPositions: any[] = [];

  totalCount = 0;
  openCount = 0;
  onHoldCount = 0;
  closeCount = 0;

  donutSegments: DonutSegment[] = [];
  positionColors = POSITION_COLORS;

  opened: boolean = false;
  isDataLoaded = false;

  @Output() hiringIdsReady = new EventEmitter<any[]>();

  constructor(
    private openPositionsService: HiringManagerOpenpositionsService,
    private router: Router,
  ) {}
  
  get positionStats() {
    return [
      { label: this.PositionLabels.Total_Positions, value: this.totalCount },
      { label: this.PositionLabels.Open_Positions, value: this.openCount },
      { label: this.PositionLabels.On_Hold_Positions, value: this.onHoldCount },
      { label: this.PositionLabels.Closed_Positions, value: this.closeCount }
    ];
  }

  ngOnInit() {
    this.loadPositionsData();
  }

  private loadPositionsData(): void {
    // Always fetch fresh data on entering the dashboard — getOpenPositions() is
    // cache-first (it only calls the API when the signal is empty), which left this
    // widget (and the hiringIds it feeds to Interviews Status) showing stale data.
    this.openPositionsService.openPositionsSignal.set(null);
    this.openPositionsService.refreshOpenPositions();

    interval(1000)
      .pipe(takeWhile(() => !this.isDataLoaded))
      .subscribe({
        next: () => {
          this.fetchPositions();
        },
        error: (error) => {
          this.isDataLoaded = true;
        },
      });
  }

  private fetchPositions(): void {
    const signalData = this.openPositionsService.openPositionsSignal();
    if (signalData && signalData?.data) {
      const openPositions = Array.isArray(signalData.data) ? signalData.data : [signalData.data];
      const hiringRequestId = openPositions?.map(
        (candidate: any) => candidate?.hiringRequestDto?.hiringRequestId,
      );
      this.hiringIdsReady.emit(hiringRequestId);
      this.allPositions = openPositions.map((item: any) => {
        let vendorNames: string[] = [];
        
        if (item.hiringVendorDtoList && Array.isArray(item.hiringVendorDtoList)) {
          vendorNames = item.hiringVendorDtoList
            .map((vendor: any) => vendor.vendorName)
            .filter((name: string) => name && name.trim());
        }
        
        return {
          ...item,
          lob: item.hiringRequestDto?.lobName,
          project: item.hiringRequestDto?.projectName,
          vendor: vendorNames,
          jobTitle: item.hiringRequestDto?.jobTitle,
          month: item.hiringRequestDto?.createdDate,
          createdDate: item.hiringRequestDto?.createdDate,
          numberOfPositions: item.hiringRequestDto?.numberOfPositions || 0,
          status: item.hiringRequestDto?.status || '',
          hiringVendorDtoList: item.hiringVendorDtoList
        };
      });

      this.filteredPositions = [...this.allPositions];
      this.updateCounts();
      this.isDataLoaded = true;
    }
  }

  onFilter(filteredPositions: any) {
    this.filteredPositions = filteredPositions.data;
    if(filteredPositions.buttonClicked === 'apply') {
      this.opened = false;
    }
    this.updateCounts();
  }

  private updateCounts(): void {
    this.totalCount = 0;
    this.openCount = 0;
    this.closeCount = 0;
    this.onHoldCount = 0;

    this.filteredPositions.forEach(position => {
      const positionCount = position?.numberOfPositions ?? 0;

      this.totalCount += positionCount;

      switch (position.status) {
        case 'Open':
          this.openCount += positionCount;
          break;
        case 'Onhold':
          this.onHoldCount += positionCount;
          break;
        case 'Closed':
          this.closeCount += positionCount;
          break;
      }
    });

    this.donutSegments = this.computeSegments(
      [this.openCount, this.onHoldCount, this.closeCount],
      [POSITION_COLORS.open, POSITION_COLORS.onHold, POSITION_COLORS.closed],
    );
  }

  private computeSegments(values: number[], colors: string[]): DonutSegment[] {
    const total = values.reduce((sum, v) => sum + v, 0);
    if (total <= 0) return [];
    const segments: DonutSegment[] = [];
    let acc = 0;
    values.forEach((value, idx) => {
      if (value <= 0) return;
      const arcLength = (value / total) * DONUT_CIRC;
      segments.push({
        color: colors[idx],
        dashArray: `${arcLength} ${DONUT_CIRC - arcLength}`,
        dashOffset: -acc,
      });
      acc += arcLength;
    });
    return segments;
  }

  navigateToPositionsList() {
    this.router.navigate(['/hiring-manager/open-positions']);
  }
}
